import { useMedplum } from '@medplum/react';
import { useState, useCallback, useEffect } from 'react';
import { ConversationThread } from '../models/conversation';
import { Communication } from '@medplum/fhirtypes';

/**
 * Hook for managing Reflection Guide threads for a client
 * Fetches all threads where:
 * - The client is the subject
 * - The thread is a top-level Communication (no partOf)
 */
export function useClientReflectionThreads() {
  const medplum = useMedplum();
  const patientId = medplum.getProfile()?.id;
  const [threads, setThreads] = useState<ConversationThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const fetchThreads = useCallback(async () => {
    if (!patientId) return;
    
    try {
      setIsLoading(true);
      
      // Find top 10 conversation threads where the client is the subject
      // Note: Uses Patient resource internally since that's what Medplum expects
      // Limit to 10 to improve performance (we only show 5 in the UI)
      const threadComms = await medplum.searchResources('Communication', {
        subject: `Patient/${patientId}`,
        'part-of:missing': true, // Get only parent communications (threads)
        _sort: '-sent', // Newest first
        _count: 10 // Limit to 10 most recent threads
      });
      
      // For each thread, get just the most recent message
      // This is more efficient than loading all messages for each thread
      const populatedThreads = await Promise.all(
        threadComms.map(async (threadComm) => {
          // Only get the most recent 2 messages for preview purposes
          const threadMessages = await medplum.searchResources('Communication', {
            'part-of': `Communication/${threadComm.id}`,
            _sort: '-sent', // Newest first (reversed from before)
            _count: 2 // Just get the most recent messages for each thread
          });
          
          // Sort them back to chronological order
          const sortedMessages = [...threadMessages].sort(
            (a, b) => new Date(a.sent || '').getTime() - new Date(b.sent || '').getTime()
          );
          
          return ConversationThread.fromCommunication({
            comm: threadComm,
            threadMessageComms: sortedMessages
          });
        })
      );
      
      setThreads(populatedThreads);
    } catch (error) {
      console.error('Error fetching client reflection threads:', error);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId]);
  
  // Create a new thread function - optimized version
  const createNewThread = useCallback(async (topic: string = 'Self-Guided Reflection') => {
    if (!patientId) {
      throw new Error('Client ID is required');
    }
    
    try {
      // Get current timestamp
      const now = new Date().toISOString();
      
      // Create the thread Communication
      // Note: Uses Patient resource internally since that's what Medplum expects
      const threadComm = await medplum.createResource({
        resourceType: 'Communication',
        status: 'completed',
        sent: now,
        subject: { reference: `Patient/${patientId}` },
        payload: [{ contentString: topic }],
        extension: [
          {
            url: 'https://progressnotes.app/fhir/StructureDefinition/reflection-thread',
            valueBoolean: true
          },
          {
            url: 'https://progressnotes.app/fhir/StructureDefinition/self-initiated',
            valueBoolean: true // Mark as client-initiated
          },
          {
            url: 'https://progressnotes.app/fhir/StructureDefinition/last-activity',
            valueDateTime: now
          }
        ]
      }) as Communication & { id: string };
      
      // Add the welcome message from the assistant
      const welcomeMessage = await medplum.createResource({
        resourceType: 'Communication',
        status: 'completed',
        sent: now,
        subject: { reference: `Patient/${patientId}` },
        sender: { reference: 'Practitioner/reflection-guide-bot' },
        payload: [{ 
          contentString: "Hello! I'm your Reflection Guide. I'm here to help you explore your thoughts and feelings. What would you like to reflect on today?" 
        }],
        partOf: [{ reference: `Communication/${threadComm.id}` }]
      });
      
      // Instead of reloading all threads, just create the new thread object and add it to state
      const newThread = ConversationThread.fromCommunication({
        comm: threadComm,
        threadMessageComms: [welcomeMessage]
      });
      
      // Update threads state directly (prepend the new thread since threads are sorted newest first)
      setThreads(prevThreads => [newThread, ...prevThreads.slice(0, 9)]);
      
      // Return the thread ID
      return threadComm.id;
    } catch (error) {
      console.error('Error creating new thread:', error);
      throw error;
    }
  }, [medplum, patientId]);
  
  // Load threads on initial render
  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);
  
  return { 
    threads, 
    isLoading, 
    fetchThreads,
    createNewThread 
  };
}